/**
 * Modal de detalle para `invoices` (tabla unificada de comprobantes — egresos
 * por foto/OCR/manual + comprobantes ingestados de SET). Lo usan AMBOS tabs de
 * `/app/facturas`:
 *  - Egresos (purchase): manual + set.
 *  - Ingresos (sale) cuando `source='set'` (las de Actuo emiten su propio modal).
 *
 * Read-only — no edita. Si la factura tiene foto (`originalFileId`), muestra un
 * botón para abrirla en un viewer aparte.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ExternalLink, ReceiptText, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DocumentClientBlock,
  DocumentMetaGrid,
  DocumentSection,
  DocumentTotals,
  formatMoney,
} from '@/components/app-document-detail';

interface InvoiceDetail {
  id: string;
  direction: 'purchase' | 'sale';
  tipoDocumento: string | null;
  invoiceNumber: string;
  timbrado: string | null;
  cdc: string | null;
  issueDate: string; // yyyy-MM-dd
  condition: string | null;
  currency: string;
  senderRuc: string | null;
  senderRazonSocial: string | null;
  receiverRuc: string | null;
  receiverRazonSocial: string | null;
  subtotalBeforeTax: string | null;
  taxExempt: string | null;
  tax5: string | null;
  tax10: string | null;
  grandTotal: string;
  status: string;
  source: 'manual' | 'set' | string;
  categoriaGasto: string | null;
  duplicateOfSet: boolean;
  originalFileId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: Record<string, any> | null;
  createdAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  pending_extraction: 'Procesando',
  pending_review: 'En revisión',
  approved: 'Aprobada',
  included_in_declaration: 'Incluida en declaración',
  rejected: 'Rechazada',
  set_synced: 'Recibida en SET',
};

function statusVariant(s: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (s === 'approved' || s === 'included_in_declaration') return 'success';
  if (s === 'rejected') return 'destructive';
  if (s === 'pending_review' || s === 'pending_extraction') return 'warning';
  return 'secondary';
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Anclamos a mediodía para evitar drift de timezone al formatear un date puro.
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return format(d, 'dd MMM yyyy', { locale: es });
}

export function InvoiceDetailDialog({
  id,
  onClose,
  onOpenFile,
}: {
  id: string | null;
  onClose: () => void;
  onOpenFile?: (fileId: string) => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['app-invoice-detail', id],
    queryFn: () => api<InvoiceDetail>(`/api/app/invoices/${id}`),
    enabled: !!id,
  });

  // Reintentar IA cuando la factura quedó "Procesando" más tiempo de lo esperado.
  const reExtract = useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>(`/api/app/invoices/${id}/re-extract`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Reintento enviado. La factura se procesa en unos segundos.');
      // Refresh el detalle (status pasará de pending_extraction a otra cosa).
      setTimeout(() => qc.invalidateQueries({ queryKey: ['app-invoice-detail', id] }), 3000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['app-invoice-detail', id] }), 8000);
      qc.invalidateQueries({ queryKey: ['app-invoices'] });
    },
    onError: () => toast.error('No se pudo reintentar la extracción'),
  });

  const open = !!id;
  const inv = data ?? null;
  const isCompra = inv?.direction === 'purchase';
  const counterparty = isCompra
    ? { name: inv?.senderRazonSocial ?? '', ruc: inv?.senderRuc ?? null }
    : { name: inv?.receiverRazonSocial ?? '', ruc: inv?.receiverRuc ?? null };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-5xl overflow-x-hidden overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCompra ? 'Detalle del egreso' : 'Detalle del ingreso'}
            {inv?.source === 'set' && (
              <Badge variant="outline" className="text-[10px]" title="Ingestada del portal SET">
                <ReceiptText className="mr-1 size-3" /> SET
              </Badge>
            )}
            {inv?.duplicateOfSet && (
              <Badge variant="secondary" className="text-[10px]">
                Duplicada
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {inv ? `Nº ${inv.invoiceNumber}` : 'Cargando…'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {isError && (
          <p className="text-sm text-destructive">No se pudo cargar el detalle.</p>
        )}

        {inv && (
          <div className="min-w-0 space-y-5">
            <DocumentClientBlock
              name={counterparty.name || '—'}
              doc={counterparty.ruc}
              badge={
                <Badge variant={statusVariant(inv.status)}>
                  {STATUS_LABEL[inv.status] ?? inv.status}
                </Badge>
              }
            />

            <DocumentSection title="Datos del comprobante">
              <DocumentMetaGrid
                items={[
                  { label: 'Nº de factura', value: inv.invoiceNumber || '—' },
                  { label: 'Fecha de emisión', value: fmtDate(inv.issueDate) },
                  { label: 'Timbrado', value: inv.timbrado || '—' },
                  { label: 'Condición', value: inv.condition === 'credito' ? 'Crédito' : inv.condition === 'contado' ? 'Contado' : '—' },
                  {
                    label: 'CDC',
                    value: inv.cdc ? (
                      <span className="break-all font-mono text-[11px]">{inv.cdc}</span>
                    ) : (
                      '—'
                    ),
                  },
                  { label: 'Origen', value: inv.source === 'set' ? 'Portal SET' : 'Carga manual' },
                  {
                    label: 'Categoría',
                    value: inv.categoriaGasto ?? (isCompra ? 'Sin categorizar' : '—'),
                  },
                ]}
              />
            </DocumentSection>

            <DocumentSection title="Totales">
              <DocumentTotals
                currency={inv.currency || 'PYG'}
                lines={[
                  { label: 'Gravada (subtotal)', value: inv.subtotalBeforeTax ?? '0' },
                  { label: 'Exentas', value: inv.taxExempt ?? '0' },
                  { label: 'IVA 5%', value: inv.tax5 ?? '0' },
                  { label: 'IVA 10%', value: inv.tax10 ?? '0' },
                  { label: 'Total', value: inv.grandTotal, emphasis: true },
                ]}
              />
              {inv.metadata?.ivaSource === 'estimated' && (
                <p className="text-xs text-muted-foreground">
                  ⚠ Sin desglose IVA en SET — el total queda como exento (no suma IVA crédito/débito). Subí
                  la foto del comprobante si querés que se calcule el desglose real.
                </p>
              )}
            </DocumentSection>

            {inv.status === 'pending_extraction' && (
              <div className="rounded-lg border border-warning/40 bg-warning/5 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-warning">
                      La IA aún no terminó de leer la factura
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Si pasaron más de unos minutos, podés reintentar el procesamiento.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => reExtract.mutate()}
                    disabled={reExtract.isPending}
                  >
                    <RotateCw className={reExtract.isPending ? 'animate-spin' : ''} />
                    {reExtract.isPending ? 'Reintentando…' : 'Reintentar IA'}
                  </Button>
                </div>
              </div>
            )}

            {inv.originalFileId && onOpenFile && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenFile(inv.originalFileId!)}
                >
                  <ExternalLink /> Ver foto del comprobante
                </Button>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              Cargado el {fmtDate(inv.createdAt)} · {formatMoney(inv.grandTotal, inv.currency || 'PYG')}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
